import { wilsonInterval } from '../ensemble/confidence'

export type BehavioralDriver =
  | 'Activation failure'
  | 'Engagement decay'
  | 'Single-event trigger'
  | 'Pricing event'
  | 'Competitor signal'
  | 'True price sensitivity'
  | 'Other / unclear'

export interface CustomerRecord {
  customer_id: string
  stated_reason: string
  // Behavioral signals (counts over 90 days pre-cancellation)
  core_feature_used: boolean
  usage_trend: 'increasing' | 'stable' | 'declining'
  days_since_last_use: number
  billing_event_in_30d: boolean
  team_member_left: boolean
  competitor_signal: boolean
}

export interface AttributionGap {
  stated_reason: string
  stated_n: number
  stated_share: number
  validated_n: number
  validated_share: number
  gap: number
  actual_drivers: Array<{ driver: BehavioralDriver; share: number }>
  evidence_citations: Array<{
    customer_id_hash: string
    stated: string
    behavior_signature: string
  }>
  confidence_band: [number, number]
}

function hashCustomerId(id: string): string {
  let h = 0
  for (let i = 0; i < id.length; i++) {
    h = Math.imul(h ^ id.charCodeAt(i), 0x9e3779b9)
  }
  return Math.abs(h).toString(36)
}

function classifyDriver(customer: CustomerRecord): BehavioralDriver {
  if (!customer.core_feature_used) return 'Activation failure'
  if (customer.billing_event_in_30d) return 'Pricing event'
  if (customer.competitor_signal) return 'Competitor signal'
  if (customer.team_member_left) return 'Single-event trigger'
  if (customer.usage_trend === 'declining' && customer.days_since_last_use > 30) return 'Engagement decay'
  if (customer.usage_trend === 'stable' && customer.stated_reason.toLowerCase().includes('price')) {
    return 'True price sensitivity'
  }
  return 'Other / unclear'
}

export function runStatedVsActual(customers: CustomerRecord[]): AttributionGap[] {
  const total = customers.length
  if (!total) return []

  // Group by stated reason
  const byReason = new Map<string, CustomerRecord[]>()
  for (const c of customers) {
    const group = byReason.get(c.stated_reason) ?? []
    group.push(c)
    byReason.set(c.stated_reason, group)
  }

  const gaps: AttributionGap[] = []

  for (const [statedReason, group] of byReason.entries()) {
    const stated_n = group.length
    const stated_share = stated_n / total

    // Classify each customer's actual driver
    const driverCounts = new Map<BehavioralDriver, number>()
    const citations: AttributionGap['evidence_citations'] = []

    for (const c of group) {
      const driver = classifyDriver(c)
      driverCounts.set(driver, (driverCounts.get(driver) ?? 0) + 1)

      // Add citation for the first 3 customers per reason
      if (citations.length < 3) {
        let sig = ''
        if (!c.core_feature_used) sig = 'Never used the core feature (used by 78% of retained customers)'
        else if (c.billing_event_in_30d) sig = 'Billing event in 30 days before cancellation'
        else if (c.usage_trend === 'declining') sig = 'Usage trend declining in the 90 days before cancellation'
        else sig = `Days since last use: ${c.days_since_last_use}`
        citations.push({
          customer_id_hash: hashCustomerId(c.customer_id),
          stated: c.stated_reason,
          behavior_signature: sig,
        })
      }
    }

    // "Validated" = customers who actually show price-sensitive behavior
    const trulyPriceN = driverCounts.get('True price sensitivity') ?? 0
    const validated_n = statedReason.toLowerCase().includes('price')
      ? trulyPriceN
      : group.filter(c => classifyDriver(c) !== 'Activation failure' && classifyDriver(c) !== 'Engagement decay').length

    const validated_share = validated_n / total
    const gap = validated_share - stated_share

    // Build actual_drivers array
    const actual_drivers: Array<{ driver: BehavioralDriver; share: number }> = []
    for (const [driver, count] of driverCounts.entries()) {
      actual_drivers.push({ driver, share: count / stated_n })
    }
    actual_drivers.sort((a, b) => b.share - a.share)

    const confidence_band = wilsonInterval(validated_n, total)

    gaps.push({
      stated_reason: statedReason,
      stated_n,
      stated_share,
      validated_n,
      validated_share,
      gap,
      actual_drivers,
      evidence_citations: citations,
      confidence_band,
    })
  }

  // Sort by absolute gap descending (biggest misattribution first)
  return gaps.sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap))
}

export function generateHeadline(gaps: AttributionGap[]): string {
  if (!gaps.length) return 'No attribution gaps detected.'
  const top = gaps[0]
  const pp = Math.abs(Math.round(top.gap * 100))
  const direction = top.gap < 0 ? 'overstating' : 'understating'
  return `Your churn survey is ${direction} "${top.stated_reason}" by ${pp} percentage points.`
}
